import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocs, collection, query, orderBy, limit } from 'firebase/firestore';
import { db } from '@/firebase';
import { useNavigate } from 'react-router-dom';
import { Car, AlertTriangle } from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';

interface VehiclesWidgetProps {
  householdId: string | null;
}

interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  currentMileage?: number;
  lastServiceDate?: string;
  createdAt?: any;
}

export const VehiclesWidget: React.FC<VehiclesWidgetProps> = ({ householdId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!householdId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getDocs(
      query(
        collection(db, 'households', householdId, 'vehicles'),
        orderBy('createdAt', 'desc'),
        limit(10)
      )
    )
      .then((snap) => {
        setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Vehicle)));
      })
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, [householdId]);

  if (!householdId) return null;

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900 rounded-2xl p-5 space-y-4 border border-slate-100 dark:border-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Car className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-black uppercase tracking-widest text-slate-400">Vehicles</span>
        </div>
        <div className="bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs px-2 py-0.5 rounded-full">
          {vehicles.length}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="space-y-2">
            <div className="h-12 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
            <div className="h-12 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
          </div>
        ) : vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <span className="text-sm text-slate-400">{t('vehicles.widget_empty', 'No vehicles registered')}</span>
            <button onClick={() => navigate('/vehicles')} className="text-xs text-brand dark:text-indigo-400 hover:underline">
              {t('vehicles.widget_add', 'Add Vehicle')} →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {vehicles.slice(0, 3).map((vehicle) => {
              const daysSince = vehicle.lastServiceDate
                ? differenceInDays(new Date(), parseISO(vehicle.lastServiceDate))
                : null;
              const serviceStatus = daysSince === null ? null : daysSince > 180 ? 'overdue' : daysSince > 150 ? 'soon' : 'ok';

              return (
                <div key={vehicle.id} className="space-y-1">
                  <div className="font-medium text-slate-900 dark:text-white text-sm flex items-center gap-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                    {serviceStatus === 'overdue' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded-full">
                        <AlertTriangle className="w-2.5 h-2.5" /> Service Due
                      </span>
                    )}
                    {serviceStatus === 'soon' && (
                      <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full">Due Soon</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400">
                    {vehicle.currentMileage ? `${vehicle.currentMileage.toLocaleString()} mi` : ''}
                    {vehicle.currentMileage && vehicle.lastServiceDate ? ' • ' : ''}
                    {vehicle.lastServiceDate ? vehicle.lastServiceDate : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto">
        <button
          onClick={() => navigate('/vehicles')}
          className="text-brand dark:text-indigo-400 text-xs font-medium hover:text-indigo-300 transition-colors"
        >
          {t('vehicles.widget_manage', 'Manage Vehicles')} →
        </button>
      </div>
    </div>
  );
};